import PropTypes from 'prop-types';
import { formatRefDate } from '../../utils/formatRefDate';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined, MoreOutlined, LoadingOutlined } from '@ant-design/icons';
import { Badge, Input, Modal, Table, Button, message, Dropdown, Select } from 'antd';
import { UpdateIOT_M_Sensor } from '../../Api/UpdateData';
import Swal from 'sweetalert2';
import SensorDetail from './SensorDetail';

const TableMonitoring = ({ dataSource, isLoading, loadingRows, updateData }) => {

    const navigate = useNavigate();
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [detail, setDetail] = useState(null);
    const [data, setData] = useState([]);
    const [loadingData, setLoadingData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);


    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = (dataSource || []).map((item) => {
                    const sensors = (item.current || []).reduce((acc, sensor) => {
                        acc[sensor.SensorCode] = sensor;
                        return acc;
                    }, {});

                    const getSensor = (code) => sensors[code] || {};

                    const lastSensor = item.current?.[item.current.length - 1] || {};

                    return {
                        ...item,
                        key: JSON.stringify([item.KodeCabang, item.ShipCode]),
                        RefDate: lastSensor.RefDate || "",
                        name: item.ShipName,
                        status: lastSensor.Status || 0,

                        tmp1: getSensor("TMP01").Value1 || 0,
                        tmp2: getSensor("TMP02").Value1 || 0,
                        rpm1: getSensor("RPM01").Value1 || 0,
                        rpm2: getSensor("RPM02").Value1 || 0,
                        level1: getSensor("LVL01").Value1 || 0,
                        level2: getSensor("LVL02").Value1 || 0, // sesuaikan kalau memang ada LVL02
                        inc1: getSensor("INC01").Value1 || 0,
                        inc2: getSensor("INC01").Value2 || 0,
                    };
                });

                setData(response);
                // console.log(response);

                // Buat data loading placeholder
                if (loadingRows && Object.keys(loadingRows).length > 0) {
                    const loadingItems = Object.keys(loadingRows).map((shipCode) => ({
                        key: `loading-${shipCode}`,
                        ShipCode: shipCode,
                        ShipName: shipCode,
                        name: shipCode,
                        isLoading: true
                    }));
                    setLoadingData(loadingItems);
                } else {
                    setLoadingData([]);
                }
            } catch (error) {
                console.log('error', error);

            }
        }

        fetchData();

    }, [dataSource, loadingRows]);

    const handleLink = (record) => {
        navigate(`/dashboard?KodeCabang=${record.KodeCabang}&ShipCode=${record.ShipCode}`)
        const payload = {
            KodeCabang: record.KodeCabang,
            NamaCabang: record.NamaCabang,
            ShipCode: record.ShipCode,
            ShipName: record.ShipName,
        }
        localStorage.setItem('Filter', JSON.stringify(payload));
    }

    const [dataSetting, setDataSetting] = useState(null);
    const [editedSensors, setEditedSensors] = useState({});

    const handleSettings = (record) => {
        setDataSetting(record);
        // Initialize edited sensors with current max values
        const initialEdited = {};
        record.current?.forEach((sensor) => {
            initialEdited[sensor.SensorCode] = {
                MaxValue1: sensor.MaxValue1 || 0,
                MaxValue2: sensor.MaxValue2 || 0,
                MaxValue3: sensor.MaxValue3 || 0,
            };
        });
        setEditedSensors(initialEdited);
        setIsModalOpen(true);
    }

    const handleMaxValueChange = (sensor, field, value) => {
        // console.log(sensor);
        setEditedSensors(prev => ({
            ...prev,
            [sensor.SensorCode]: {
                ...prev[sensor.SensorCode],
                [field]: value === '' ? 0 : parseFloat(value) || 0
            }
        }));
    }

    const handleSaveSensorSettings = async () => {
        try {
            const params = {
                AccountId: dataSetting.KodeCabang,
                ObjectCode: dataSetting.ShipCode,
            }
            const payload = dataSetting.current.map((sensor) => ({
                AccountId: dataSetting.KodeCabang,
                ObjectCode: dataSetting.ShipCode,
                SensorId: sensor.SensorCode,
                MaxValue1: editedSensors[sensor.SensorCode]?.MaxValue1 || sensor.MaxValue1,
                MaxValue2: editedSensors[sensor.SensorCode]?.MaxValue2 || sensor.MaxValue2,
                MaxValue3: editedSensors[sensor.SensorCode]?.MaxValue3 || sensor.MaxValue3,
            }));
            // console.log('payload', payload);
            const response = await UpdateIOT_M_Sensor(params, payload);
            if (response.status === 200) {
                // console.log('response', response);
                Swal.fire({
                    icon: 'success',
                    title: 'Success !',
                    text: response.data.message,
                    confirmButtonText: 'OK',
                }).then(() => {
                    setIsModalOpen(false);
                    updateData(true);
                });
            }
        } catch (error) {
            console.log('error', error);
            message.error('Failed to update !');
        }
    }

    const columns = [
        { title: 'Vessel', dataIndex: 'name', key: 'vessel', width: 150,
            render: (value, record) => record.isLoading ? <span className="monitoring-row-loading" role="status"><LoadingOutlined spin aria-hidden="true" />Loading {value}...</span> :
                <button className="monitoring-vessel-link" onClick={() => handleLink(record)}>{value || record.ShipCode || '\u2014'}</button> },
        { title: 'Status', dataIndex: 'status', width: 90, render: (value, row) => row.isLoading ? '\u2014' : <Badge status={value ? 'success' : 'error'} text={value ? 'Online' : 'Offline'} /> },
        { title: 'Last Reading', dataIndex: 'RefDate', width: 165, render: formatRefDate },
        { title: 'Actions', key: 'actions', width: 70, render: (_, record) =>
            <Dropdown trigger={['click']} disabled={record.isLoading} menu={{ items: ['RPM', 'Temperature', 'Level', 'Inclination', 'Settings'].map(key => ({ key, label: key })),
                onClick: ({ key }) => key === 'Settings' ? handleSettings(record) : setDetail({ record, kind: key }) }}>
                <Button type="text" aria-label={`Actions for ${record.ShipName || record.ShipCode}`} icon={<MoreOutlined />} />
            </Dropdown> },
    ];
    const filteredData = data.filter(item =>
        [item.ShipName, item.ShipCode, item.KodeCabang].some(value => String(value || '').toLowerCase().includes(searchText.toLowerCase())) &&
        (statusFilter === 'all' || (statusFilter === 'online' ? Boolean(item.status) : !item.status))
    );
    const pending = loadingData.filter(item => String(item.ShipCode || '').toLowerCase().includes(searchText.toLowerCase()));
    const combinedData = [...filteredData, ...pending];
    return (
        <>
            <div className="monitoring-list-filters">
                <Input aria-label="Search vessels" allowClear prefix={<SearchOutlined />} placeholder="Search vessel..." value={searchText} onChange={event => { setSearchText(event.target.value); setPage(1); }} />
                <Select aria-label="Filter vessel status" value={statusFilter} onChange={value => { setStatusFilter(value); setPage(1); }}
                    options={[{ value: 'all', label: 'All Status' }, { value: 'online', label: 'Online' }, { value: 'offline', label: 'Offline' }]} />
            </div>
            <Table size="small" dataSource={combinedData} columns={columns} loading={isLoading} scroll={{ x: 475 }}
                pagination={{ current: page, pageSize: 10, showSizeChanger: false, onChange: setPage, showTotal: total => `${total} vessels` }}
                locale={{ emptyText: 'No vessels match the current filter' }} />
            <Modal rootClassName="monitoring-detail-modal" title={detail ? `${detail.record.ShipName || detail.record.ShipCode} · ${detail.kind}` : ''}
                open={Boolean(detail)} onCancel={() => setDetail(null)} footer={null} width={760} destroyOnHidden>
                {detail && <><p className="monitoring-note">Last Reading: {formatRefDate(detail.record.RefDate)}</p><SensorDetail record={detail.record} kind={detail.kind} /></>}
            </Modal>
            <Modal
                rootClassName="monitoring-settings-modal"
                centered
                title={dataSetting?.ShipName && (
                    <div className="pb-4 border-b">
                        <h2 className="text-lg font-bold text-gray-800">SETTING {dataSetting.ShipName}</h2>
                        <p className="text-sm text-gray-500">Last Update: {formatRefDate(dataSetting.RefDate)}</p>
                    </div>
                )}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                width={1200}
                styles={{
                    body: {
                        maxHeight: '70vh',
                        overflowY: 'auto'
                    }
                }}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                        Cancel
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveSensorSettings}>
                        Save Setting
                    </Button>,
                ]}
            >
                <div className="flex flex-col gap-4">


                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {dataSetting?.current?.map((sensor, index) => (
                            <div key={index} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                {/* Header */}
                                <div className="mb-4 pb-3 border-b">
                                    <p className="font-bold text-gray-800">{sensor.SensorCode}</p>
                                    <p className="text-xs text-gray-500">{sensor.DisplayGroupName}</p>
                                </div>

                                {/* Current Values Display */}
                                {/* <div className="space-y-3 mb-4 pb-4 border-b bg-gray-50 p-3 rounded">
                                    {sensor.Value1 !== 0 || sensor.MaxValue1 !== 0 ? (
                                        <div>
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-sm font-medium text-gray-700">Value 1</span>
                                                <span className="text-sm font-bold text-blue-600">
                                                    {sensor.Value1.toFixed(2)} / {sensor.MaxValue1}
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className={`h-2.5 rounded-full transition-all ${sensor.Value1 > sensor.MaxValue1 * 0.9 ? 'bg-red-500' :
                                                            sensor.Value1 > sensor.MaxValue1 * 0.7 ? 'bg-yellow-500' :
                                                                'bg-green-500'
                                                        }`}
                                                    style={{
                                                        width: `${Math.min((sensor.Value1 / sensor.MaxValue1) * 100, 100)}%`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    ) : null}

                                    {sensor.Value2 !== 0 || sensor.MaxValue2 !== 0 ? (
                                        <div>
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-sm font-medium text-gray-700">Value 2</span>
                                                <span className="text-sm font-bold text-blue-600">
                                                    {sensor.Value2.toFixed(2)} / {sensor.MaxValue2}
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className={`h-2.5 rounded-full transition-all ${sensor.Value2 > sensor.MaxValue2 * 0.9 ? 'bg-red-500' :
                                                            sensor.Value2 > sensor.MaxValue2 * 0.7 ? 'bg-yellow-500' :
                                                                'bg-green-500'
                                                        }`}
                                                    style={{
                                                        width: `${Math.min((sensor.Value2 / sensor.MaxValue2) * 100, 100)}%`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    ) : null}

                                    {sensor.Value3 !== 0 || sensor.MaxValue3 !== 0 ? (
                                        <div>
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-sm font-medium text-gray-700">Value 3</span>
                                                <span className="text-sm font-bold text-blue-600">
                                                    {sensor.Value3.toFixed(2)} / {sensor.MaxValue3}
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className={`h-2.5 rounded-full transition-all ${sensor.Value3 > sensor.MaxValue3 * 0.9 ? 'bg-red-500' :
                                                            sensor.Value3 > sensor.MaxValue3 * 0.7 ? 'bg-yellow-500' :
                                                                'bg-green-500'
                                                        }`}
                                                    style={{
                                                        width: `${Math.min((sensor.Value3 / sensor.MaxValue3) * 100, 100)}%`
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div> */}

                                {/* Input Fields for Max Values */}
                                <div className="space-y-3">
                                    <p className="text-sm font-semibold text-gray-800">Edit Max Values</p>

                                    {sensor.MaxValue1 !== 0 ? (
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 block mb-1">Max Value 1</label>
                                            <Input
                                                type="number"
                                                placeholder="Enter Max Value 1"
                                                value={editedSensors[sensor.SensorCode]?.MaxValue1 || ''}
                                                onChange={(e) => handleMaxValueChange(sensor, 'MaxValue1', e.target.value)}
                                                className="w-full"
                                            />
                                        </div>
                                    ) : null}

                                    {sensor.MaxValue2 !== 0 ? (
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 block mb-1">Max Value 2</label>
                                            <Input
                                                type="number"
                                                placeholder="Enter Max Value 2"
                                                value={editedSensors[sensor.SensorCode]?.MaxValue2 || ''}
                                                onChange={(e) => handleMaxValueChange(sensor, 'MaxValue2', e.target.value)}
                                                className="w-full"
                                            />
                                        </div>
                                    ) : null}

                                    {sensor.MaxValue3 !== 0 ? (
                                        <div>
                                            <label className="text-xs font-medium text-gray-700 block mb-1">Max Value 3</label>
                                            <Input
                                                type="number"
                                                placeholder="Enter Max Value 3"
                                                value={editedSensors[sensor.SensorCode]?.MaxValue3 || ''}
                                                onChange={(e) => handleMaxValueChange(sensor, 'MaxValue3', e.target.value)}
                                                className="w-full"
                                            />
                                        </div>
                                    ) : null}
                                </div>

                                {/* Status */}
                                <div className="mt-3 pt-3 border-t flex items-center justify-between">
                                    <span className="text-xs text-gray-600">Status</span>
                                    <Badge
                                        status={sensor.Status ? 'success' : 'error'}
                                        text={sensor.Status ? 'Active' : 'Inactive'}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </>
    )
}

TableMonitoring.propTypes = { dataSource: PropTypes.array, isLoading: PropTypes.bool, loadingRows: PropTypes.object, updateData: PropTypes.func };
export default TableMonitoring
